import { Component, HostBinding, ViewEncapsulation } from '@angular/core';
import { finalize, map, Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { RouterLink } from '@angular/router';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UNIT_OPTIONS } from '../../../shared/models/list';
import { AUCTION_OFFER_FIELD, AuctionOffer, Bundle } from '../../models/auction-offer';
import { AuctionOfferStatus } from '../../models/auction-constants';
import { getAuctionOfferStatusTr } from '../utils/auction.utils';
import { AuctionOfferService } from '../../services/auction-offer.service';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { TranslationService } from '../../../shared/services/translation.service';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { Material } from '../../models/material';
import { UNDERSCORE } from '../../../shared/utils/constant';

@Component({
	selector: 'copo-bid-list',
	templateUrl: './bid-list.component.html',
	styleUrl: './bid-list.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		BackButtonComponent,
		TranslateModule,
		VirtualScrollerDirective,
		NgFor,
		MatIconModule,

		MatTooltipModule,

		MatButtonModule,
		NgIf,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe,
		RouterLink
	]
})
export class BidListComponent {
	@HostBinding('class.passthrough') componentClass = true;

	readonly UNIT_OPTIONS = UNIT_OPTIONS;
	readonly UNDERSCORE = UNDERSCORE;
	isLoading = true;

	filters = '';
	user: User;

	constructor(
		private readonly auctionOfferService: AuctionOfferService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
	) {
		this.user = this.userService.user;
	}

	get dataId() {
		return `bid-${ this.filters }`;
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<AuctionOffer[]>) => void) {
		provideData(
			this.auctionOfferService.getAuctionOffers(
				AUCTION_OFFER_FIELD,
				this.filters,
				index,
				limit,
				undefined,
				false,
				false
			).pipe(
				finalize(() => {
					this.isLoading = false;
				}),
				map(res =>
					res.results
						.filter(auctionOffer =>
							auctionOffer.bundles.some(bundle =>
								bundle.bids.length > 0))
						.sort((offerA, offerB) =>
							this.getLastUpdateDate(offerB.bundles) - this.getLastUpdateDate(offerA.bundles)))
			)
		);
	}

	getLastUpdateDate(bundles: Bundle[]): number {
		return Math.max(...bundles.flatMap(bundle =>
			bundle.bids.map(bid => new Date(bid.creationDate).getTime())), 0);
	}

	isAwarded(auctionOffer: AuctionOffer) {
		return auctionOffer.status === AuctionOfferStatus.AWARDED;
	}

	isPending(auctionOffer: AuctionOffer) {
		return auctionOffer.status === AuctionOfferStatus.PENDING;
	}

	isRejected(auctionOffer: AuctionOffer) {
		return auctionOffer.status === AuctionOfferStatus.REJECTED;
	}

	getAuctionOfferStatus(status: AuctionOfferStatus) {
		return getAuctionOfferStatusTr(this.translationService, status);
	}

	getSteelGradeDescription(materials: Material[]): string {
		return materials
			.map(material => material.material.steelgradeCommercialDescription || UNDERSCORE)
			.join(', ');
	}
}
